import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckIcon } from '@phosphor-icons/react';
import { AuthService } from '@/api';
import { CreateOrgDialog } from '@/components/nav/nav-menu/create-org-dialog';
import { authClient, useSession } from '@/lib/auth-client';
import { logspot } from '@logspot/web';
import { LinkLogo } from '@/components/nav/link-logo';
import GoogleLogo from '@/images/google-logo.svg?react';
import { z } from 'zod';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAppForm } from '@/hooks/use-app-form';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MagicLinkForm } from '@/components/auth/magic-link-form';
import { toast } from 'sonner';
import { getAuthCallbackURL, redirectToAuthTarget } from '@/lib/auth-redirect';
import { sessionOptions, signUpOffersOptions } from '@/hooks/query-options';
import { formatCurrency } from '@/lib/currency';
import {
  formatBucketLabel,
  getMonthlyPrice,
  getYearlyPrice,
  INTRO_OFFER_PRICE,
  INTRO_OFFER_SCOPE,
  introOfferCovers,
  pricingLinkPath,
  pricingLinkSearch,
} from '@/lib/plans';

const signUpSchema = z.object({
  name: z.string({ message: 'Please enter your name.' }).min(2, {
    message: 'Name must be at least 2 characters.',
  }),
  email: z.string({ message: 'Please enter your email address.' }).email({
    message: 'Please enter a valid email address.',
  }),
  password: z.string({ message: 'Please enter your password.' }).min(8, {
    message: 'Please enter a password that is at least eight characters.',
  }),
});

export const Route = createFileRoute('/sign-up')({
  component: SignUp,
  validateSearch: z.object({
    redirect: z.string().optional(),
    // Pricing-page fast lane: the picked plan rides through verification into onboarding.
    ...pricingLinkSearch,
  }),
  beforeLoad: async ({ context, search }) => {
    // Pricing and redirect links carry a signed-in user on; a plain visit shows AlreadySignedIn instead.
    if (!search.plan && !search.redirect) return;
    const session = await context.queryClient
      .ensureQueryData(sessionOptions())
      .catch(() => null);
    if (session?.user) {
      redirectToAuthTarget(search.redirect ?? pricingLinkPath('/getting-started', search));
    }
  },
});

const usd = (dollars: number) => formatCurrency(dollars, 'USD');

function SignUp() {
  const { redirect: explicitRedirect, plan, interval, events } = Route.useSearch();
  const redirectTo = explicitRedirect ?? pricingLinkPath('/onboarding', { plan, interval, events });
  // After email verification, better-auth redirects here. New sign-ups have
  // no org, so default straight into onboarding unless an explicit redirect
  // target was provided (e.g. they were bounced from a deep link).
  const callbackURL = getAuthCallbackURL(redirectTo, '/onboarding');
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { data: offers } = useQuery(signUpOffersOptions());
  const introOffer = offers?.introOffer === true;
  const bucket = events ?? 10_000;
  const planPrice = !plan
    ? ''
    : interval === 'yearly'
      ? `${usd(getYearlyPrice(plan, bucket))} per year`
      : `${usd(getMonthlyPrice(plan, bucket))} per month`;

  const [isLoading, setIsLoading] = useState(false);

  const form = useAppForm({
    defaultValues: {
      name: '',
      email: '',
      password: '',
    } as z.infer<typeof signUpSchema>,
    validators: { onSubmit: signUpSchema },
    onSubmit: async ({ value }) => {
      const { name, email, password } = value;
      setIsLoading(true);

      await authClient.signUp.email({
        email,
        password,
        name,
        callbackURL,
      }, {
        onSuccess: async (ctx) => {
          // Better-auth returns the created user on signup even when
          // requireEmailVerification is on (only the session is withheld),
          // so we can still tag identity once here.
          logspot.register({ email, authed: true, userId: ctx.data?.user?.id });
          logspot.unregister('demo');
          await navigate({ to: '/verify-email', search: { email, redirect: redirectTo } });
        },
        onError: (ctx) => {
          setIsLoading(false);
          const errorMessage = ctx.error.message || '';
          if (errorMessage.includes('already exists') || ctx.error.status === 409) {
            toast.error('Account already exists', { description: 'Try signing in instead.' });
          } else {
            toast.error('Sign up failed', { description: errorMessage || 'Please try again.' });
          }
        },
      });
    },
  });

  // A one-click demo session is anonymous, so the demo visitor still gets the sign-up form.
  if (session?.user && !session.user.isAnonymous) {
    return <AlreadySignedIn email={session.user.email} />;
  }

  return (
    <section className="py-12 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-sm">
          <div className="flex flex-col items-center">
            <LinkLogo />
            <h1 className="mt-12 text-3xl font-bold">
              {plan ? 'Create Your Account' : 'Sign Up For Free'}
            </h1>
            <p className="text-muted-foreground mt-4 text-center font-medium">
              {plan
                ? 'Add your website next, then confirm your plan.'
                : 'Web and product analytics simplified.'}
            </p>
            {!plan && (
              <>
                <div className="text-muted-foreground mt-5 flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-sm font-medium">
                  {['Free plan forever', '14-day Essentials trial', 'No card needed'].map((point) => (
                    <span key={point} className="flex items-center gap-1.5">
                      <CheckIcon weight="bold" className="text-chart-green size-3.5" />
                      {point}
                    </span>
                  ))}
                </div>
                {introOffer && (
                  <p className="text-muted-foreground mt-2 text-center text-sm">
                    Upgrade during your trial and your first month is{' '}
                    <span className="text-foreground font-semibold">{INTRO_OFFER_PRICE}</span>{' '}
                    {INTRO_OFFER_SCOPE}.
                  </p>
                )}
              </>
            )}
            {plan && (
              <div className="border-primary/45 bg-primary/5 mt-6 flex w-full items-center gap-3 rounded border p-3">
                <span className="flex flex-1 flex-col">
                  <span className="text-sm font-semibold">
                    Plan: {plan === 'pro' ? 'Pro' : 'Essentials'}, Billed{' '}
                    {interval === 'yearly' ? 'Yearly' : 'Monthly'}
                  </span>
                  <span className="text-muted-foreground text-sm">
                    Up to {formatBucketLabel(bucket)} monthly events ·{' '}
                    {introOffer && introOfferCovers(bucket)
                      ? `First month ${INTRO_OFFER_PRICE}, then ${planPrice}`
                      : planPrice}
                  </span>
                </span>
              </div>
            )}
            {plan && (
              <p className="text-muted-foreground mt-2 text-center text-sm">
                You can change the plan, or start free instead, before checkout.
              </p>
            )}
          </div>

          <div className="mt-12">
            <Button
              className="w-full"
              size="lg"
              type="button"
              variant="outline"
              onClick={async () => {
                logspot.track({
                  event: 'Sign Up Started',
                  metadata: { method: 'google' },
                });
                await authClient.signIn.social({
                  provider: 'google',
                  callbackURL,
                });
              }}
            >
              <GoogleLogo className="mr-2 size-5" />
              Sign Up With Google
            </Button>
          </div>

          <div className="relative mt-6">
            <div className="absolute inset-0 flex items-center">
              <div className="border-border w-full border-t"></div>
            </div>

            <div className="relative flex justify-center">
              <span className="px-2"> or </span>
            </div>
          </div>

          <Tabs defaultValue="password" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="password">Password</TabsTrigger>
              <TabsTrigger value="magic-link">Magic Link</TabsTrigger>
            </TabsList>

            <TabsContent value="password">
              <form
                className="mt-4 space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  form.handleSubmit();
                }}
              >
                <form.AppForm>
                  <form.AppField name="name">
                    {(field) => (
                      <field.Input label="Name" className="h-10" placeholder="Your Name..." />
                    )}
                  </form.AppField>
                  <form.AppField name="email">
                    {(field) => (
                      <field.Input label="Email" className="h-10" autoComplete="email" placeholder="Enter Your Email Address..." />
                    )}
                  </form.AppField>
                  <form.AppField name="password">
                    {(field) => (
                      <field.Input label="Password" className="h-10" type="password" autoComplete="new-password" placeholder="Enter a Password..." />
                    )}
                  </form.AppField>
                </form.AppForm>
                <Button
                  className="w-full"
                  size="lg"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating account...' : 'Sign Up With Email'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="magic-link">
              <MagicLinkForm callbackURL={callbackURL} intent="sign-up" />
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground font-medium">
              Do you already have an account?{' '}
              <Link to="/sign-in" search={{ redirect: redirectTo }}>
                <Button className="px-1" variant="link">
                  {' '}
                  Sign In{' '}
                </Button>
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function AlreadySignedIn({ email }: { email: string }) {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  return (
    <section className="py-12 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-sm px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center">
          <LinkLogo />
          <h1 className="mt-12 text-3xl font-bold">
            You&apos;re Already Signed In
          </h1>
          <p className="text-muted-foreground mt-4 font-medium">
            You&apos;re signed in as {email}. Add another workspace to this
            account, or sign out to sign up with a different email.
          </p>
          <Button className="mt-8 w-full" onClick={() => setCreating(true)}>
            Add Another Workspace
          </Button>
          <Button
            className="mt-3 w-full"
            variant="outline"
            loading={signingOut}
            onClick={() => {
              setSigningOut(true);
              void AuthService.logout(queryClient, '/sign-up');
            }}
          >
            Sign Out &amp; Use Another Email
          </Button>
          <Button asChild variant="ghost" className="mt-3 w-full">
            <Link to="/">Back to My Dashboard</Link>
          </Button>
        </div>
      </div>
      <CreateOrgDialog open={creating} onOpenChange={setCreating} />
    </section>
  );
}
